import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../../../libs/css/bootstrap.min3.3.7.css";
import "../../../libs/css/sweetalert2.min.css";
import { API_ROUTES } from "../../../utils/constants";
import {
  confirmPasswordValidation,
  emailValidation,
  passwordValidation,
} from "../../../utils/Input";
import Footers from "../Footers";
import HeaderTop from "../HeaderTop";
import Loaders from "../Loaders";
import Menus from "../Menus";
import SubUsersTable from "./SubUsersTable";

export default function SubUsers(props) {
  const {
    objectToFormData,
    errorFunction,
    Helmet,
    capitalizeWords,
    isAuthExpired,
  } = props;
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Manage Sub Users | Forthsoft";
  }, []);

  const userData = JSON.parse(localStorage.getItem("userDataFs"));

  // Only accounts with parent_id of null (main/parent accounts) may manage sub users
  useEffect(() => {
    if (userData?.responseDetails?.parent_id != null) {
      navigate("/dashboard");
    }
  }, []);

  const [dataList, setDataList] = useState([]);
  const [dataTotal, setDataTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const countPerPage = 20;

  const [isEditing, setIsEditing] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [submitBtnText, setSubmitBtnText] = useState("Create Sub User");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Bumped every time the form switches mode (create <-> edit) so the form
  // card remounts and its fade/slide-in animation replays, making the change visible.
  const [formAnimKey, setFormAnimKey] = useState(0);

  const {
    register,
    reset,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
    },
  });

  const getData = async () => {
    try {
      const response = await fetch(API_ROUTES.VIEW_SUB_USERS, {
        method: "POST",
        headers: { token: userData?.responseDetails?.token },
      });
      const responseJSON = await response.json();
      isAuthExpired(responseJSON); // check for expired login

      if (responseJSON.status.toString() === "200") {
        setDataList(responseJSON.data);
        setDataTotal(responseJSON.data.length);
      } else {
        setDataList([]);
        setDataTotal(0);
      }
      setIsLoading(false);
    } catch (error) {
      console.log("Error getting data: ", error);
      setDataList([]);
      setDataTotal(0);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const resetForm = () => {
    reset({ name: "", email: "", password: "", password_confirmation: "" });
    setIsEditing(0);
    setEditingId(null);
    setSubmitBtnText("Create Sub User");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFormAnimKey((prev) => prev + 1);
  };

  const editSubUser = (row) => {
    // A deleted sub user (status 0) can't be edited
    if (row.status !== 1) {
      return;
    }
    reset({
      name: row.name,
      email: row.email,
      password: "",
      password_confirmation: "",
    });
    setIsEditing(1);
    setEditingId(row.id);
    setSubmitBtnText("Update Sub User");
    setFormAnimKey((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitSubUser = async (data) => {
    const isUpdate = isEditing === 1;
    const url = isUpdate
      ? API_ROUTES.EDIT_SUB_USER
      : API_ROUTES.CREATE_SUB_USER;
    const customData = isUpdate
      ? { id: editingId, name: data.name, email: data.email }
      : {
          name: data.name,
          email: data.email,
          password: data.password,
          password_confirmation: data.password_confirmation,
        };

    try {
      const result = await Swal.fire({
        title: `${isUpdate ? "Update" : "Create"} this sub user?`,
        text: "This can always be undone, proceed?",
        icon: "question",
        iconHtml: "?",
        showCancelButton: true,
        confirmButtonColor: "#337ab7",
        cancelButtonColor: "#ccc",
        confirmButtonText: `Yes, Proceed`,
      });

      if (result.isConfirmed) {
        Swal.fire({
          title: "Processing...",
          text: "Please wait a second for a response...",
          icon: "success",
          showConfirmButton: false,
          confirmButtonColor: "#027937",
          cancelButtonColor: "#d33",
        });

        const response = await fetch(url, {
          method: "POST",
          body: objectToFormData(customData),
          headers: { token: userData?.responseDetails?.token },
        });
        const responseJSON = await response.json();
        isAuthExpired(responseJSON); // check for expired login

        if (responseJSON.status.toString() === "200") {
          Swal.fire({
            title: "Successful",
            text:
              responseJSON.message ||
              `Sub user has been ${isUpdate ? "updated" : "created"}`,
            icon: "success",
            timer: 4000,
          });
          resetForm();
          getData();
        } else {
          if (responseJSON.status.toString() === "500") {
            Swal.fire({
              title: "Error",
              text: responseJSON.required_fields,
              icon: "error",
            });
            return;
          }
          Swal.fire({
            title: "Error",
            text: responseJSON.message,
            icon: "error",
          });
        }
      }
    } catch (error) {
      if (error.response) {
        errorFunction(error);
      }
    }
  };

  const deleteSubUser = async (row) => {
    try {
      const result = await Swal.fire({
        title: `Delete ${row.name}?`,
        text: "This cannot be undone, proceed?",
        icon: "question",
        iconHtml: "?",
        showCancelButton: true,
        confirmButtonColor: "red",
        cancelButtonColor: "#ccc",
        confirmButtonText: `Yes, Proceed`,
      });

      if (result.isConfirmed) {
        Swal.fire({
          title: "Processing...",
          text: "Please wait a second for a response...",
          icon: "success",
          showConfirmButton: false,
          confirmButtonColor: "#027937",
          cancelButtonColor: "#d33",
        });

        const response = await fetch(API_ROUTES.DELETE_SUB_USER, {
          method: "POST",
          body: objectToFormData({ id: row.id }),
          headers: { token: userData?.responseDetails?.token },
        });
        const responseJSON = await response.json();
        isAuthExpired(responseJSON); // check for expired login

        if (responseJSON.status.toString() === "200") {
          Swal.fire({
            title: "Deleted",
            text: responseJSON.message || "Sub-user has been deleted",
            icon: "success",
            timer: 2000,
          });
          if (editingId === row.id) {
            resetForm();
          }
          getData();
        } else {
          if (responseJSON.status.toString() === "500") {
            Swal.fire({
              title: "Error",
              text: responseJSON.required_fields,
              icon: "error",
            });
            return;
          }
          Swal.fire({
            title: "Error",
            text: responseJSON.message,
            icon: "error",
          });
        }
      }
    } catch (error) {
      if (error.response) {
        errorFunction(error);
      }
    }
  };

  return (
    <>
      <Loaders />

      <style jsx>{`
        @keyframes subUserFormSlideFade {
          0% {
            opacity: 0;
            transform: translateY(18px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .subuser-form-anim {
          animation: subUserFormSlideFade 0.35s ease;
        }
      `}</style>

      <div className="flapt-page-wrapper mainContent">
        <Menus />

        <div className="flapt-page-content">
          <HeaderTop />

          <div className="main-content introduction-farm mt--20">
            <div className="content-wraper-area">
              <div className="dashboard-area">
                <div className="container-fluid mb-90">
                  <div className="row g-4">
                    <div className="col-lg-12 pl-xs-0 pr-xs-0">
                      <div className="card-header mb-20 mb-xs-10">
                        <h2>Manage Sub Users</h2>
                      </div>

                      <div
                        className="card mb-30 subuser-form-anim"
                        key={formAnimKey}
                      >
                        <div className="card-header">
                          <h3>{submitBtnText}</h3>
                        </div>
                        <div className="card-body formInputs">
                          <form
                            className="row g-3"
                            onSubmit={handleSubmit(submitSubUser)}
                          >
                            <div className="col-md-6 mb-15">
                              <label className="form-label" htmlFor="name">
                                Full Name
                              </label>
                              {errors.name && (
                                <label className="errors">
                                  {errors.name?.message}
                                </label>
                              )}

                              <input
                                className="form-control"
                                id="name"
                                name="name"
                                type="text"
                                placeholder="Enter Full Name"
                                {...register("name", {
                                  required: "Name is required",
                                })}
                              />
                            </div>

                            <div className="col-md-6 mb-15">
                              <label className="form-label" htmlFor="email">
                                Email Address
                              </label>
                              {errors.email && (
                                <label className="errors">
                                  {errors.email?.message}
                                </label>
                              )}

                              <input
                                className="form-control"
                                id="email"
                                name="email"
                                type="email"
                                placeholder="Enter Email Address"
                                {...register("email", {
                                  ...emailValidation,
                                })}
                              />
                            </div>

                            {isEditing === 0 && (
                              <>
                                <div className="col-md-6 mb-15">
                                  <label
                                    className="form-label"
                                    htmlFor="password"
                                  >
                                    Password
                                  </label>
                                  {errors.password && (
                                    <label className="errors">
                                      {errors.password?.message}
                                    </label>
                                  )}

                                  <div style={{ position: "relative" }}>
                                    <input
                                      className="form-control"
                                      id="password"
                                      name="password"
                                      type={showPassword ? "text" : "password"}
                                      placeholder="Enter Password"
                                      style={{ paddingRight: "50px" }}
                                      {...register("password", {
                                        ...passwordValidation,
                                      })}
                                    />
                                    <span
                                      onClick={() =>
                                        setShowPassword(!showPassword)
                                      }
                                      style={{
                                        position: "absolute",
                                        top: "50%",
                                        right: "15px",
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "#069",
                                        userSelect: "none",
                                      }}
                                    >
                                      {showPassword ? "Hide" : "Show"}
                                    </span>
                                  </div>
                                </div>

                                <div className="col-md-6 mb-15">
                                  <label
                                    className="form-label"
                                    htmlFor="password_confirmation"
                                  >
                                    Confirm Password
                                  </label>
                                  {errors.password_confirmation && (
                                    <label className="errors">
                                      {errors.password_confirmation?.message}
                                    </label>
                                  )}

                                  <div style={{ position: "relative" }}>
                                    <input
                                      className="form-control"
                                      id="password_confirmation"
                                      name="password_confirmation"
                                      type={
                                        showConfirmPassword
                                          ? "text"
                                          : "password"
                                      }
                                      placeholder="Confirm Password"
                                      style={{ paddingRight: "50px" }}
                                      {...register("password_confirmation", {
                                        ...confirmPasswordValidation,
                                        validate: (value) => {
                                          const pass = getValues("password");
                                          if (value === pass) {
                                            return true;
                                          } else {
                                            return "Passwords do not match";
                                          }
                                        },
                                      })}
                                    />
                                    <span
                                      onClick={() =>
                                        setShowConfirmPassword(
                                          !showConfirmPassword,
                                        )
                                      }
                                      style={{
                                        position: "absolute",
                                        top: "50%",
                                        right: "15px",
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "#069",
                                        userSelect: "none",
                                      }}
                                    >
                                      {showConfirmPassword ? "Hide" : "Show"}
                                    </span>
                                  </div>
                                </div>
                              </>
                            )}

                            <div className="col-md-12 text-end pr-10 mt-15 ">
                              {isEditing === 1 && (
                                <button
                                  className="btn btn-light"
                                  type="button"
                                  onClick={resetForm}
                                  style={{
                                    marginRight: "10px",
                                    marginBottom: "10px",
                                  }}
                                >
                                  Cancel
                                </button>
                              )}
                              <button
                                className="btn btn-primary btn-primaryi"
                                type="submit"
                                disabled={isSubmitting || !isValid}
                                style={{ opacity: isSubmitting ? "0.5" : 1 }}
                              >
                                {submitBtnText}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>

                      <SubUsersTable
                        dataList={dataList}
                        dataTotal={dataTotal}
                        isLoading={isLoading}
                        countPerPage={countPerPage}
                        setPage={setPage}
                        editSubUser={editSubUser}
                        deleteSubUser={deleteSubUser}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footers />
          </div>
        </div>
      </div>
    </>
  );
}
